import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ArrowRight, Sliders, Waveform, MusicNotes, Lightning, CheckCircle, Hourglass, Lock, Sparkle } from "@phosphor-icons/react";

const TOOLS = [
  { status: "ready", title: "Escribe tu propia letra (sin costo de IA)",
    desc: "Pega o escribe tu letra completa con tags [Verse], [Chorus], [Bridge]. La IA solo arma el audio respetando lo que tú creaste. Tu tiempo de estudio paga solo los minutos en sesión.",
    icon: Sparkle },
  { status: "ready", title: "Asistente IA de letra (opcional, bajo demanda)",
    desc: "Si te atoraste, pulsas 'Generar letra con IA' y Claude la compone respetando tu estructura, tonalidad y BPM. Solo se ejecuta cuando tú lo decides — nada de gasto silencioso.",
    icon: Sparkle },
  { status: "ready", title: "Selector de progresión de acordes",
    desc: "Ingresa progresiones con números romanos (I-V-vi-IV) o cifrado real (Am-F-C-G). Aplicamos tensiones de jazz si las pides (9, 11, 13).",
    icon: MusicNotes },
  { status: "ready", title: "Tonalidad y modos griegos",
    desc: "Tonalidad completa: mayor, menor, dórico, frigio, lidio, mixolidio, eólico, locrio. La IA respeta tu elección.",
    icon: Waveform },
  { status: "ready", title: "Editor de estructura por bloques",
    desc: "Define la línea de tiempo arrastrando bloques: Intro · Verso · Pre-Coro · Coro · Puente · Solo · Outro. La letra y el audio respetan esta estructura.",
    icon: Sliders },
  { status: "ready", title: "BPM exacto y métricas irregulares",
    desc: "Tempo numérico (40-240 BPM). Compases compuestos e irregulares: 3/4, 5/4, 6/8, 7/8, 12/8.",
    icon: Lightning },
  { status: "ready", title: "Asignación de instrumentos personalizada",
    desc: "Describe el timbre exacto: 'sintetizador analógico de los 80', 'guitarra acústica con cuerdas de nylon', 'bajo eléctrico con fuzz'.",
    icon: MusicNotes },
  { status: "soon", title: "Exportación por STEMS separados (WAV 24-bit)",
    desc: "Voces, batería, bajo y melodía en pistas independientes para mezclar en tu DAW. Trabajando en la integración con Moises / Demucs.",
    icon: Waveform },
  { status: "soon", title: "Separador de pistas integrado",
    desc: "Sube un audio externo y desmezcla voces e instrumentos. Mismo motor que los STEMS.",
    icon: Waveform },
  { status: "future", title: "Inpainting melódico",
    desc: "Regenerar solo un compás específico (ej. 'rehaz solo este solo de guitarra, más rápido'). Requiere proveedor de audio que aún no expone esta capacidad.",
    icon: Sparkle },
  { status: "future", title: "Audio-to-audio",
    desc: "Tararea una melodía o sube un riff de guitarra como referencia. Requiere proveedor de audio compatible.",
    icon: Sparkle },
  { status: "future", title: "Exportación MIDI · Piano Roll · Partitura · WebMIDI / VST",
    desc: "Notación tradicional, tablaturas, archivos MIDI editables y conexión con teclados controladores. Roadmap: 2026 Q4 al sumar un motor sinfónico.",
    icon: MusicNotes },
];

export default function StudioInfo() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 w-fit px-3 py-2 -ml-3 rounded-full hover:bg-white/5" data-testid="studio-info-back">
          <ArrowLeft size={16} weight="bold" /> Volver al inicio
        </Link>

        {/* Hero */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFB800]/10 border border-[#FFB800]/30 mb-5">
            <Sliders weight="fill" size={14} className="text-[#FFB800]" />
            <span className="text-xs uppercase tracking-widest text-[#FFB800] font-bold">Para músicos profesionales</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] mb-5">
            Studio Pro <span className="text-[#FFB800]">a tu medida</span>
          </h1>
          <p className="text-base sm:text-lg text-white/75 leading-relaxed max-w-3xl">
            ¿Eres productor o sabes manejar progresiones de acordes, tonalidades y métricas?
            Olvídate del flujo de un solo clic. Aquí controlas <span className="text-[#B4F34A] font-semibold">cada detalle armónico, estructural e instrumental</span> y pagas solo por el tiempo que estás creando.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to={user ? "/studio/buy" : "/register"} className="px-7 py-4 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black font-bold active:scale-95 transition-all inline-flex items-center justify-center gap-2" data-testid="studio-info-cta-buy">
              Comprar tiempo de estudio <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="#tools" onClick={(e) => { e.preventDefault(); document.getElementById("tools")?.scrollIntoView({ behavior: "smooth" }); }} className="px-7 py-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium">
              Ver herramientas
            </Link>
          </div>
          <p className="mt-3 text-xs text-white/40">$8 MXN/min · paquetes desde $120 MXN · sin contrato</p>
        </div>

        {/* Pricing summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-14">
          <Pack name="Demo" min="15 min" price="$120" sub="Para probar" />
          <Pack name="Sesión" min="1 hora" price="$450" sub="−6%" />
          <Pack name="Sesión" min="3 horas" price="$1,200" sub="−17% · popular" highlight />
          <Pack name="Día completo" min="8 horas" price="$2,800" sub="−27%" />
        </div>

        {/* Tools list */}
        <h2 id="tools" className="font-display text-3xl font-bold mb-2">Herramientas profesionales</h2>
        <p className="text-white/50 mb-6 text-sm">
          Te decimos exactamente qué está listo, qué llega pronto y qué está en el roadmap. <span className="text-white">No prometemos lo que no tenemos.</span>
        </p>

        <div className="space-y-3">
          {TOOLS.map(t => <Tool key={t.title} {...t} />)}
        </div>

        {/* Final CTA */}
        <div className="mt-14 rounded-3xl bg-gradient-to-br from-[#1a1405] to-[#0a0a0a] border border-[#FFB800]/30 p-8 sm:p-10 text-center">
          <h3 className="font-display text-3xl sm:text-4xl font-black mb-3">¿Listo para producir?</h3>
          <p className="text-white/70 mb-6 max-w-xl mx-auto">
            Compra un paquete de tiempo y comienza a generar canciones con control profesional. Tu reloj solo corre cuando estás en el estudio.
          </p>
          <Link to={user ? "/studio/buy" : "/register"} className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black font-bold active:scale-95" data-testid="studio-info-cta-bottom">
            {user ? "Comprar tiempo ahora" : "Crear cuenta"} <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function Pack({ name, min, price, sub, highlight }) {
  return (
    <div className={`rounded-2xl p-4 border ${highlight ? "bg-[#FFB800]/10 border-[#FFB800]/40" : "bg-[#121212] border-white/10"}`}>
      <p className="text-[10px] uppercase tracking-widest text-white/50">{name}</p>
      <p className="font-display font-bold text-lg">{min}</p>
      <p className="font-display font-black text-2xl mt-1" style={{ color: highlight ? "#FFB800" : "#fff" }}>{price}</p>
      <p className="text-xs text-[#B4F34A] mt-1">{sub}</p>
    </div>
  );
}

function Tool({ status, title, desc, icon: Icon }) {
  const palette = {
    ready: { tag: "Disponible ya", bg: "bg-[#B4F34A]/10 border-[#B4F34A]/30", t: "text-[#B4F34A]", IconCmp: CheckCircle },
    soon:  { tag: "Próximamente", bg: "bg-[#FFB800]/10 border-[#FFB800]/30", t: "text-[#FFB800]", IconCmp: Hourglass },
    future:{ tag: "Roadmap", bg: "bg-white/5 border-white/10", t: "text-white/40", IconCmp: Lock },
  }[status];
  return (
    <div className={`rounded-2xl ${palette.bg} border p-5 flex gap-4`}>
      <div className="shrink-0 w-12 h-12 rounded-xl bg-black/40 flex items-center justify-center">
        <Icon size={22} weight="duotone" className={palette.t} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <p className="font-display font-bold text-base sm:text-lg">{title}</p>
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-black/40 ${palette.t} flex items-center gap-1`}>
            <palette.IconCmp size={10} weight="fill" /> {palette.tag}
          </span>
        </div>
        <p className="text-sm text-white/65 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}
